/**
 * 通知域 Service 层
 * 根据 notification/frontend-spec.md 规范定义
 */

import { config } from '@/config';
import * as notificationMock from '@/mocks/notification';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import type { ApiResponse, PageData } from '@/types/common';
import type {
  NotificationRecordDTO,
  QueryNotificationRecordPageRequest,
  ResendNotificationRequest,
  SendNotificationRequest,
  QueryNotificationStatisticsRequest,
  NotificationStatisticsDTO,
} from '@/types/notification/notificationRecord';
import type {
  NotificationTemplateDTO,
  QueryNotificationTemplatePageRequest,
  UpdateNotificationTemplateRequest,
} from '@/types/notification/notificationTemplate';

/**
 * 分页查询通知记录
 */
export async function pageNotificationRecords(
  params: QueryNotificationRecordPageRequest
): Promise<ApiResponse<PageData<NotificationRecordDTO>>> {
  if (config.mock) {
    return notificationMock.mockPageNotificationRecords(params);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.RECORD_PAGE, params);
}

/**
 * 查询通知详情
 */
export async function getNotificationRecordById(
  notificationId: string
): Promise<ApiResponse<NotificationRecordDTO>> {
  if (config.mock) {
    return notificationMock.mockGetNotificationRecordById(notificationId);
  }
  return httpClient.get(`${API_ENDPOINTS.NOTIFICATION.RECORD_BASE}/${notificationId}`);
}

/**
 * 重发通知
 */
export async function resendNotifications(
  data: ResendNotificationRequest
): Promise<ApiResponse<null>> {
  if (config.mock) {
    return notificationMock.mockResendNotifications(data);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.RECORD_RESEND, data);
}

/**
 * 手动发送通知
 */
export async function sendNotification(
  data: SendNotificationRequest
): Promise<ApiResponse<NotificationRecordDTO>> {
  if (config.mock) {
    return notificationMock.mockSendNotification(data);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.SEND, data);
}

/**
 * 查询发送统计
 */
export async function queryNotificationStatistics(
  params: QueryNotificationStatisticsRequest
): Promise<ApiResponse<NotificationStatisticsDTO[]>> {
  if (config.mock) {
    return notificationMock.mockQueryNotificationStatistics(params);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.STATISTICS, params);
}

/**
 * 分页查询通知模板
 */
export async function pageNotificationTemplates(
  params: QueryNotificationTemplatePageRequest
): Promise<ApiResponse<PageData<NotificationTemplateDTO>>> {
  if (config.mock) {
    return notificationMock.mockPageNotificationTemplates(params);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.TEMPLATE_PAGE, params);
}

/**
 * 编辑通知模板
 */
export async function updateNotificationTemplate(
  data: UpdateNotificationTemplateRequest
): Promise<ApiResponse<NotificationTemplateDTO>> {
  if (config.mock) {
    return notificationMock.mockUpdateNotificationTemplate(data);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.TEMPLATE_UPDATE, data);
}

/**
 * 标记单条通知为已读
 */
export async function markNotificationAsRead(
  notificationId: string
): Promise<ApiResponse<null>> {
  if (config.mock) {
    return notificationMock.mockMarkNotificationAsRead(notificationId);
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.RECORD_MARK_READ, { notificationId });
}

/**
 * 一键已读
 */
export async function markAllNotificationsAsRead(): Promise<ApiResponse<null>> {
  if (config.mock) {
    return notificationMock.mockMarkAllNotificationsAsRead();
  }
  return httpClient.post(API_ENDPOINTS.NOTIFICATION.RECORD_MARK_ALL_READ);
}

/**
 * 获取未读通知数量
 */
export async function getUnreadNotificationCount(): Promise<ApiResponse<number>> {
  if (config.mock) {
    return notificationMock.mockGetUnreadNotificationCount();
  }
  return httpClient.get(API_ENDPOINTS.NOTIFICATION.RECORD_UNREAD_COUNT);
}
